export type GPUTier = 'low' | 'medium' | 'high'

interface NavigatorWithMemory extends Navigator {
  deviceMemory?: number
}

let cachedTier: GPUTier | null = null

function readGPURenderer(): string {
  try {
    const canvas = document.createElement('canvas')
    const gl =
      (canvas.getContext('webgl2') as WebGL2RenderingContext | null) ||
      (canvas.getContext('webgl') as WebGLRenderingContext | null)
    if (!gl) return ''
    const ext = gl.getExtension('WEBGL_debug_renderer_info')
    if (!ext) return ''
    const renderer = gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)
    return typeof renderer === 'string' ? renderer.toLowerCase() : ''
  } catch {
    return ''
  }
}

function classifyGPU(rendererStr: string): GPUTier | null {
  if (!rendererStr) return null
  // Low-end / very old / mobile-class
  if (
    /apple a[0-9](\D|$)|adreno [1-4]\d{2}|mali-[4-8]\d{2}|powervr|swiftshader|llvmpipe|software/.test(
      rendererStr
    )
  ) {
    return 'low'
  }
  // Integrated / older laptop GPUs
  if (
    /intel|hd graphics|uhd graphics|iris|geforce (gt|mx) ?[0-9]|radeon (hd|r[2-5])/.test(
      rendererStr
    )
  ) {
    return 'medium'
  }
  // Modern dedicated
  if (
    /rtx|gtx 10|gtx 16|gtx 20|gtx 30|radeon rx|m1|m2|m3|m4|apple gpu/.test(
      rendererStr
    )
  ) {
    return 'high'
  }
  return null
}

function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia('(pointer: coarse)').matches ||
    window.matchMedia('(max-width: 768px)').matches
  )
}

export function detectGPUTier(): GPUTier {
  if (cachedTier) return cachedTier
  if (typeof window === 'undefined') return 'medium'

  const cores = navigator.hardwareConcurrency ?? 4
  const memory = (navigator as NavigatorWithMemory).deviceMemory ?? 4
  const gpuClass = classifyGPU(readGPURenderer())

  // Mobile: don't blanket-force 'low'. Forcing DPR 1 on retina phones was the
  // root cause of the blurry mobile render. Capable phones (strong GPU or
  // enough cores/RAM) get 'medium' for a sharper DPR; only genuinely weak
  // phones stay 'low'. The runtime FpsReporter still freezes a struggling
  // device into static mode if frames tank.
  if (isTouchDevice()) {
    if (gpuClass === 'high') {
      cachedTier = 'medium'
    } else if (gpuClass === 'low' || cores <= 4 || memory <= 3) {
      cachedTier = 'low'
    } else {
      cachedTier = 'medium'
    }
    return cachedTier
  }

  if (cores <= 2 || memory <= 2) {
    cachedTier = 'low'
    return cachedTier
  }

  if (gpuClass === 'low') {
    cachedTier = 'low'
    return cachedTier
  }
  if (gpuClass === 'medium' || cores <= 4 || memory <= 4) {
    cachedTier = 'medium'
    return cachedTier
  }
  cachedTier = gpuClass ?? 'high'
  return cachedTier
}

export function getDPRForTier(tier: GPUTier): [number, number] {
  // Cap by the real device pixel ratio so we never upscale past native, but
  // allow up to 2x for crisp rendering on high-density (retina) screens.
  const cap =
    typeof window !== 'undefined' && window.devicePixelRatio
      ? window.devicePixelRatio
      : 1
  switch (tier) {
    case 'low':
      return [1, Math.min(cap, 1.5)]
    case 'medium':
      return [1, Math.min(cap, 2)]
    case 'high':
      return [1, Math.min(cap, 2)]
  }
}